import React, { ReactElement, useContext } from 'react';
import { defineMessages, t } from '@sm/intl';
import { Immutable } from '@wds/utils';
import { Box } from '@wds/box';
import { IconCaretDown } from '@wds/icons';
import { Menu } from '@wds/menu';
import { List } from '@wds/list';
import { Accordion } from '@wds/accordion';
import { Responsive } from '@wds/responsive';
import {
  generateMetricsAttribute,
  MetricsTracker,
  USER_EVENTS,
} from '@sm/metrics';
import { SubMenuItem } from '../mobile-nav/SubMenuItem';
import { NavButton } from '../nav-button';

import { Route } from '../../types';
import HeaderContext from '../../HeaderContext';

const COPY = defineMessages({
  OPEN_USER_MENU: {
    id: 'RenderUserMenu.Open',
    defaultMessage: 'Open user menu',
    description:
      '[Type: Label][Vis.: high] - Button that opens and closes the user menu.',
  },
  EXPAND_ARROW_ICON: {
    id: 'RenderUserMenu.Dropdown',
    defaultMessage: 'Dropdown arrow icon',
    description: '[Type: Label][Vis.: high] - Expand arrow icon.',
  },
});

const UserDropDownTrigger = (username: string) => {
  return (
    <NavButton label={t(COPY.OPEN_USER_MENU)}>
      {username}
      <Box ml={2}>
        <IconCaretDown title={t(COPY.EXPAND_ARROW_ICON)} />
      </Box>
    </NavButton>
  );
};

export type RenderUserMenuProps = Immutable<{
  username: string;
  menuItems: Route[];
}>;

const RenderUserMenu = ({
  username,
  menuItems = [],
}: RenderUserMenuProps): ReactElement | null => {
  const { pageURL, pageURLPath } = useContext(HeaderContext);
  const TrackMenuEvents = (opened: boolean) => {
    MetricsTracker.track({
      name: USER_EVENTS.ELEMENT_INTERACTION,
      data: {
        url: pageURL,
        pageName: pageURLPath,
        amplitudeEvent: 'header clicked menu',
        menuName: 'username',
        opened,
      },
    });
  };

  return (
    <>
      <Responsive minWidth="md">
        {matches =>
          matches ? (
            <Menu
              placement="bottom-right"
              menuTrigger={UserDropDownTrigger(username)}
              noRules
              elevation={9999}
              onChange={showMenu => TrackMenuEvents(showMenu)}
            >
              {menuItems.map(item => (
                <Menu.Item
                  key={item.name}
                  navigationTarget={item.path}
                  isNavigable
                  data-sm-metrics={generateMetricsAttribute({
                    data: {
                      url: pageURL,
                      pageName: pageURLPath,
                      amplitudeEvent: 'header clicked navigation',
                      navItem: item.name.toLowerCase(),
                    },
                  })}
                >
                  {item.name}
                </Menu.Item>
              ))}
            </Menu>
          ) : (
            <Accordion interactive onChange={e => TrackMenuEvents(!e.length)}>
              <Accordion.Item title={username}>
                <List noRules interactive>
                  {menuItems
                    .filter(item => item.path.indexOf('sign-out') === -1)
                    .map(item => (
                      <SubMenuItem
                        key={item.name}
                        {...item}
                        data-sm-metrics={generateMetricsAttribute({
                          data: {
                            url: pageURL,
                            pageName: pageURLPath,
                            amplitudeEvent: 'header clicked navigation',
                            navItem: item.name.toLowerCase(),
                          },
                        })}
                      />
                    ))}
                </List>
              </Accordion.Item>
            </Accordion>
          )
        }
      </Responsive>
    </>
  );
};

export default RenderUserMenu;
